import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutGrid, List, Search, ArrowDownUp, Loader2, BarChart3, X } from "lucide-react";
import api from "../lib/api";
import { ArchiveTile, ArchiveRow, Timeline, MonthNav, MONTHS } from "../components/ArchiveParts";

const PAGE = 60;
const monthKey = (iso) => iso?.slice(0, 7);

export default function Archive() {
  const [sp, setSp] = useSearchParams();
  const user = sp.get("user") || "";
  const y = Number(sp.get("y")) || null;
  const m = Number(sp.get("m")) || null;
  const sort = sp.get("sort") === "asc" ? "asc" : "desc";
  const q = sp.get("q") || "";
  const [view, setView] = useState(() => localStorage.getItem("kv-archive-view") || "grid");
  const [showTl, setShowTl] = useState(() => localStorage.getItem("kv-archive-tl") !== "0");
  const [months, setMonths] = useState([]);
  const [items, setItems] = useState([]);
  const [more, setMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(q);
  const sentinel = useRef(null);
  const req = useRef(0);

  const patch = (o) => {
    const n = new URLSearchParams(sp);
    Object.entries(o).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setSp(n, { replace: true });
  };
  const pick = (yy, mm) => { patch({ y: yy, m: mm }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  useEffect(() => { localStorage.setItem("kv-archive-view", view); }, [view]);
  useEffect(() => { localStorage.setItem("kv-archive-tl", showTl ? "1" : "0"); }, [showTl]);
  useEffect(() => { api.get("/archive/months", { params: { user: user || undefined } }).then((r) => setMonths(r.data)).catch(() => setMonths([])); }, [user]);
  useEffect(() => { const t = setTimeout(() => search !== q && patch({ q: search.trim() }), 400); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const load = useCallback(async (reset) => {
    const id = ++req.current;
    setLoading(true);
    try {
      const { data } = await api.get("/archive/items", { params: { user: user || undefined, year: y || undefined, month: m || undefined, q: q || undefined, sort, skip: reset ? 0 : items.length, limit: PAGE } });
      if (id !== req.current) return;
      setItems((prev) => (reset ? data : [...prev, ...data.filter((d) => !prev.some((p) => p.id === d.id))]));
      setMore(data.length === PAGE);
    } catch { if (id === req.current) setMore(false); }
    if (id === req.current) setLoading(false);
  }, [user, y, m, q, sort, items.length]);

  useEffect(() => { setItems([]); setMore(true); load(true); }, [user, y, m, q, sort]); // eslint-disable-line

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting && more && !loading) load(false); }, { rootMargin: "1000px" });
    io.observe(el);
    return () => io.disconnect();
  }, [more, loading, load]);

  const groups = [];
  items.forEach((it) => {
    const k = monthKey(it.created_at);
    if (!groups.length || groups[groups.length - 1].k !== k) groups.push({ k, list: [] });
    groups[groups.length - 1].list.push(it);
  });
  const periodLabel = y ? `${m ? MONTHS[m - 1] + " " : ""}${y}` : "All time";
  const periodCount = y ? months.filter((x) => x.year === y && (!m || x.month === m)).reduce((a, x) => a + x.count, 0) : months.reduce((a, x) => a + x.count, 0);
  const tbtn = (on) => `p-2 rounded-lg border transition-colors ${on ? "border-[rgb(var(--kv-glow)/0.6)] text-white bg-[rgb(var(--kv-glow)/0.12)]" : "border-white/10 text-[#A1A1AA] hover:text-white"}`;

  return (
    <div data-testid="archive-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[rgb(var(--kv-glow))]">{user ? `@${user}` : "KinkVault"} · Archive</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-white tracking-tight mt-1">{periodLabel}</h1>
        <p data-testid="archive-period-count" className="font-mono text-xs text-[#71717A] mt-2">{periodCount} items
          {user && <button data-testid="archive-all-users" onClick={() => patch({ user: "" })} className="ml-3 text-[rgb(var(--kv-glow))] hover:underline">View the entire site archive →</button>}
        </p>
      </div>

      <div className="sticky top-16 z-20 flex flex-wrap items-center gap-2 mb-5 py-2 bg-[#0A0A0A]/85 backdrop-blur-xl">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <input data-testid="archive-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${periodLabel.toLowerCase()}…`}
            className="w-full bg-[#111] border border-white/10 rounded-lg pl-9 pr-8 py-2 text-sm text-white focus:outline-none focus:border-[rgb(var(--kv-glow)/0.6)]" />
          {search && <button data-testid="archive-search-clear" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"><X size={14} /></button>}
        </div>
        <button data-testid="archive-sort-toggle" onClick={() => patch({ sort: sort === "asc" ? "" : "asc" })} className={`${tbtn(false)} flex items-center gap-1.5 text-xs`}>
          <ArrowDownUp size={14} /> {sort === "asc" ? "Oldest first" : "Newest first"}
        </button>
        <button data-testid="archive-timeline-toggle" onClick={() => setShowTl((v) => !v)} className={tbtn(showTl)} title="Timeline"><BarChart3 size={16} /></button>
        <button data-testid="archive-view-grid" onClick={() => setView("grid")} className={tbtn(view === "grid")} title="Grid view"><LayoutGrid size={16} /></button>
        <button data-testid="archive-view-list" onClick={() => setView("list")} className={tbtn(view === "list")} title="List view"><List size={16} /></button>
      </div>

      <div className="lg:hidden flex gap-2 overflow-x-auto pb-3 mb-3" data-testid="archive-month-chips">
        <button onClick={() => pick(null, null)} className={`shrink-0 px-3 py-1.5 rounded-full text-xs border ${!y ? "border-[rgb(var(--kv-glow)/0.6)] text-white" : "border-white/10 text-[#A1A1AA]"}`}>All</button>
        {months.map((x) => (
          <button key={`${x.year}-${x.month}`} onClick={() => pick(x.year, x.month)} className={`shrink-0 px-3 py-1.5 rounded-full text-xs border ${y === x.year && m === x.month ? "border-[rgb(var(--kv-glow)/0.6)] text-white" : "border-white/10 text-[#A1A1AA]"}`}>
            {MONTHS[x.month - 1].slice(0, 3)} {x.year} · {x.count}
          </button>
        ))}
      </div>

      <div className="flex gap-8">
        <aside className="hidden lg:block w-56 shrink-0"><div className="sticky top-32 max-h-[calc(100vh-9rem)] overflow-y-auto pr-1"><MonthNav months={months} sel={{ y, m }} onPick={pick} /></div></aside>
        <div className="flex-1 min-w-0">
          {showTl && <Timeline months={months} sel={{ y, m }} onPick={pick} />}
          {groups.map((g) => (
            <section key={g.k} className="mb-8" data-testid={`archive-group-${g.k}`}>
              <h2 className="font-heading text-base md:text-lg text-white mb-3 flex items-baseline gap-3">
                {MONTHS[Number(g.k.slice(5, 7)) - 1]} {g.k.slice(0, 4)}
                <span className="font-mono text-[10px] text-[#52525B]">{months.find((x) => `${x.year}-${String(x.month).padStart(2, "0")}` === g.k)?.count ?? ""} items</span>
              </h2>
              {view === "grid" ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 xl:grid-cols-6 gap-1.5 sm:gap-2">{g.list.map((it) => <ArchiveTile key={it.id} m={it} />)}</div>
              ) : (
                <div className="space-y-2">{g.list.map((it) => <ArchiveRow key={it.id} m={it} />)}</div>
              )}
            </section>
          ))}
          {!loading && !items.length && <p data-testid="archive-empty" className="text-center text-[#71717A] py-20">Nothing archived for this period{q ? ` matching “${q}”` : ""}.</p>}
          <div ref={sentinel} className="flex justify-center py-8">{loading && <Loader2 data-testid="archive-loading" className="animate-spin text-[rgb(var(--kv-glow))]" />}</div>
        </div>
      </div>
    </div>
  );
}
